import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';
import { questsApi } from '../quests/api';
import { QuestForm, newSplitDraft, type QuestFormBody } from './QuestForm';

interface Team { id: string; name: string; slug: string }

export function QuestNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const teams = useQuery({ queryKey: ['teams'], queryFn: () => api<{ teams: Team[] }>('/api/teams') });
  const [teamId, setTeamId] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (body: QuestFormBody) => {
      const payload: Parameters<typeof questsApi.create>[0] = {
        team_id: teamId,
        title: body.title,
        description: body.description,
        event_type: body.event_type,
        splits: body.splits,
        mode: body.mode,
        max_completions: body.max_completions,
        review_mode: body.review_mode,
        repeatable: body.repeatable,
        due_at: body.due_at,
      };
      if (body.mode === 'assigned' && body.assign_to.length > 0) {
        payload.assign_to = body.assign_to;
      }
      return questsApi.create(payload);
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['admin-quests'] });
      navigate(`/admin/quests/${r.quest.id}`);
    },
  });

  return (
    <div className="space-y-6">
      <header>
        <Link to="/admin/quests" className="text-sm text-ink-muted hover:text-ink">← Quests</Link>
        <h1 className="mt-1 text-2xl font-semibold">New quest</h1>
        <p className="text-ink-muted">Define a piece of work players can pick up.</p>
      </header>

      {/* v0.8.9: the field set now lives in the shared QuestForm
          so create and edit stay in sync. Create supplies the
          team picker (showTeam) and requires a team to be
          chosen before submit. */}
      <QuestForm
        initial={{
          title: '',
          description: '',
          event_type: 'quest.completed',
          splits: [newSplitDraft()],
          mode: 'open',
          max_completions: 1,
          review_mode: 'auto',
          repeatable: false,
          due_at: '',
          assign_to: [],
        }}
        showTeam
        teamId={teamId}
        onTeamChange={setTeamId}
        teams={teams.data?.teams ?? []}
        submitLabel="Create quest"
        submitting={create.isPending}
        error={err}
        submitDisabled={!teamId}
        cancelHref="/admin/quests"
        onSubmit={(body) => {
          setErr(null);
          create.mutate(body, {
            onError: (e) => setErr(e instanceof ApiError ? e.message : 'Create failed.'),
          });
        }}
      />
    </div>
  );
}
